import React, { createContext, useState, useContext, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { logEvent } from '../lib/errorLogger';

const AuthContext = createContext();

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// O banner de "sessão expirada" só faz sentido em áreas protegidas (que exigem login).
// Nas páginas públicas do Marketplace (homepage, /precos, /viaturas, etc.) um token
// expirado deixado no browser NÃO deve mostrar o banner nem convidar a fazer login.
const PROTECTED_PREFIXES = ['/marketplace-admin', '/admin', '/dashboard'];
const isProtectedSurface = () => {
  try {
    const p = window.location.pathname || '/';
    return PROTECTED_PREFIXES.some((pre) => p === pre || p.startsWith(pre + '/') || p.startsWith(pre));
  } catch (_) {
    return false;
  }
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [sessionExpired, setSessionExpired] = useState(false);
  const interceptorRef = useRef(null);
  const sessionCheckInProgressRef = useRef(false);

  // Setup global axios response interceptor - NEVER forces logout
  useEffect(() => {
    interceptorRef.current = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const url = error.config?.url || 'unknown';
        const method = error.config?.method?.toUpperCase() || '?';

        // Network error or timeout - log only
        if (!error.response) {
          logEvent('NETWORK_ERROR', { url, method, msg: error.message });
          return Promise.reject(error);
        }

        const status = error.response.status;

        // Log all API errors for diagnostics
        if (status >= 400) {
          logEvent('API_ERROR', { url, method, status, detail: error.response.data?.detail });
        }

        // On 401: só mostramos o banner em áreas protegidas. Nas páginas públicas
        // (homepage, /precos, /viaturas, etc.) um token expirado é ignorado silenciosamente.
        if (status === 401) {
          if (!isProtectedSurface()) {
            return Promise.reject(error);
          }

          // Skip if already checking or already expired
          if (sessionCheckInProgressRef.current || sessionExpired) {
            return Promise.reject(error);
          }

          // Verify: is the token truly invalid?
          sessionCheckInProgressRef.current = true;
          try {
            const savedToken = localStorage.getItem('token');
            if (savedToken) {
              await axios.get(`${API}/auth/me`, {
                headers: { Authorization: `Bearer ${savedToken}` },
                timeout: 10000,
              });
              // Token is valid - this was a transient error
              logEvent('AUTH_RECOVERED', { msg: 'Token valid despite 401 on ' + url });
              sessionCheckInProgressRef.current = false;
              return Promise.reject(error);
            }
          } catch (verifyErr) {
            if (verifyErr.response?.status === 401) {
              // Token truly invalid - show banner, do NOT redirect
              logEvent('AUTH_SESSION_EXPIRED', { msg: 'Session expired - showing banner', url });
              setSessionExpired(true);
            }
            // If network error on verification, do nothing (might be temp issue)
          }
          sessionCheckInProgressRef.current = false;
        }

        return Promise.reject(error);
      }
    );

    return () => {
      if (interceptorRef.current !== null) {
        axios.interceptors.response.eject(interceptorRef.current);
      }
    };
  }, [sessionExpired]);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async (retryCount = 0) => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      setSessionExpired(false);
      // Cache user data for resilience
      localStorage.setItem('user', JSON.stringify(response.data));
      const savedOrg = localStorage.getItem('organization');
      if (savedOrg) {
        setOrganization(JSON.parse(savedOrg));
      }
      // Inicializar push notifications na app nativa (sessão restaurada)
      try {
        const { initPushNotifications } = await import('../utils/pushNotifications');
        initPushNotifications();
      } catch (_) { /* ignore */ }
    } catch (error) {
      // On ANY error, always try to use cached user data first
      const cachedUser = localStorage.getItem('user');
      
      if (!error.response) {
        // Network error - server unreachable
        if (retryCount < 2) {
          logEvent('AUTH_RETRY', { msg: `fetchUser retry ${retryCount + 1}/2 - network error` });
          await new Promise(r => setTimeout(r, 2000));
          return fetchUser(retryCount + 1);
        }
        // Use cached user - keep user logged in
        if (cachedUser) {
          try {
            setUser(JSON.parse(cachedUser));
            logEvent('AUTH_CACHED', { msg: 'Using cached user - server unreachable' });
          } catch (e) { /* ignore */ }
        }
        return;
      }
      
      if (error.response?.status === 401) {
        if (retryCount < 1) {
          logEvent('AUTH_RETRY', { msg: 'fetchUser retry after 401' });
          await new Promise(r => setTimeout(r, 1500));
          return fetchUser(retryCount + 1);
        }
        // Token truly invalid - but still use cached user to prevent redirect
        // Só mostramos o banner em áreas protegidas (nunca nas páginas públicas).
        logEvent('AUTH_SESSION_EXPIRED', { msg: 'Token invalid - showing banner' });
        if (cachedUser) {
          try { setUser(JSON.parse(cachedUser)); } catch (e) { /* ignore */ }
        }
        if (isProtectedSurface()) {
          setSessionExpired(true);
        }
        return;
      }
      
      // Other errors (500, etc.) - use cached user
      if (cachedUser) {
        try { setUser(JSON.parse(cachedUser)); } catch (e) { /* ignore */ }
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      
      if (response.data.requires_2fa) {
        return { success: false, requires2FA: true, email: response.data.email };
      }
      
      const { access_token, user: userData, must_change_password, organization: orgData } = response.data;
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      if (orgData) {
        localStorage.setItem('organization', JSON.stringify(orgData));
        setOrganization(orgData);
      } else {
        localStorage.removeItem('organization');
        setOrganization(null);
      }
      setToken(access_token);
      setUser(userData);
      setSessionExpired(false);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

      // Inicializar push notifications (apenas na app nativa Capacitor)
      try {
        const { initPushNotifications } = await import('../utils/pushNotifications');
        initPushNotifications();
      } catch (_) { /* ignore */ }
      
      if (must_change_password) {
        return { success: true, user: userData, mustChangePassword: true, token: access_token };
      }
      
      return { success: true, user: userData, organization: orgData };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Erro ao fazer login' };
    }
  };

  const register = async (nome, email, telefone, password, consentimento_rgpd, idioma) => {
    try {
      const response = await axios.post(`${API}/auth/register`, {
        nome, email, telefone, password, consentimento_rgpd, idioma,
      });
      
      if (response.status === 202) {
        return { success: true, pending: true, message: response.data?.detail };
      }
      
      const { access_token, user: userData } = response.data;
      if (access_token) {
        localStorage.setItem('token', access_token);
        setToken(access_token);
        setUser(userData);
        axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      }
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Erro ao registar' };
    }
  };

  const logout = useCallback(async () => {
    // Desregistar token push (best-effort)
    try {
      const { unregisterPush } = await import('../utils/pushNotifications');
      await unregisterPush();
    } catch (_) { /* ignore */ }

    localStorage.removeItem('token');
    localStorage.removeItem('organization');
    setToken(null);
    setUser(null);
    setOrganization(null);
    setSessionExpired(false);
    delete axios.defaults.headers.common['Authorization'];
  }, []);

  const handleRelogin = useCallback(() => {
    logout();
    window.location.href = '/login';
  }, [logout]);

  return (
    <AuthContext.Provider value={{ user, organization, loading, login, register, logout, sessionExpired, handleRelogin }}>
      {/* Banner de sessão expirada — apenas em áreas protegidas */}
      {sessionExpired && isProtectedSurface() && (
        <div
          data-testid="session-expired-banner"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 99999,
            background: 'linear-gradient(135deg, #dc2626, #b91c1c)',
            color: 'white',
            padding: '12px 20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '16px',
            fontSize: '14px',
            fontWeight: 500,
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          }}
        >
          <span>A sua sessão expirou. Por favor, faça login novamente.</span>
          <button
            onClick={handleRelogin}
            data-testid="session-relogin-btn"
            style={{
              background: 'white',
              color: '#dc2626',
              border: 'none',
              borderRadius: '6px',
              padding: '6px 16px',
              fontWeight: 700,
              cursor: 'pointer',
              fontSize: '13px',
            }}
          >
            Fazer Login
          </button>
        </div>
      )}
      {children}
    </AuthContext.Provider>
  );
};
